import { AuthService, FeatureToggleService, FooterService, SessionService } from '@agenda-customer-ui/data-access';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ITokens, IUrlInfo } from '@core/interface';
import { TranslateService } from '@ngx-translate/core';
import { isSourceFordPass, isSourcePerks } from '@core/utils';
import { firstValueFrom } from 'rxjs';
import { MyGarageService, UserService as CoreUserService } from '@core/services';
import { getLangByCountry } from './utils/utils';
import { AppMaintenancePageComponent } from '../../components/app-maintenance/app-maintenance.component';
import { CommonModule } from '@angular/common';
import { StatusDashboardComponent } from './modules/status/components/dashboard/dashboard.component';
import { StorageKeys } from '@core/enums';
import { Router } from '@angular/router';

@Component({
  selector: 'agenda-customer-ui-status-project',
  templateUrl: './status.component.html',
  styleUrls: ['./status.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, StatusDashboardComponent, AppMaintenancePageComponent],
  providers: [AuthService, FeatureToggleService, TranslateService]
})
export class StatusProjectComponent implements OnInit {
  nationalId = '';

  #tokens!: ITokens;
  #urlInfo!: IUrlInfo;

  isSourceFordPass = false;
  isPerks = false;
  language!: string;

  hasParams = false;
  showProject = true;

  readonly #coreUserService = inject(CoreUserService);
  readonly #myGarageService = inject(MyGarageService);

  constructor(
    private auth: AuthService,
    private translate: TranslateService,
    private featureToggle: FeatureToggleService,
    private footerService: FooterService,
    private sessionService: SessionService,
    private router: Router
  ) {
    this.footerService.showFooter = false;
  }

  ngOnInit(): void {
    this.hasParams = false;

    this.auth.setParameters().subscribe({
      next: param => {
        if (JSON.stringify(param) === '{}') {
          return;
        }

        const country = param['country'];
        const source = param['source']

        if (!country || !source) {
          console.log('Country or source not found')
          return;
        }

        sessionStorage.setItem('token', param['token']);
        sessionStorage.setItem('scheduleId', param['scheduleId']);
        this.sessionService.setItem(StorageKeys.COUNTRY, param['country']);
        this.sessionService.setItem(StorageKeys.CAT, param['cat']);
        this.sessionService.setItem(StorageKeys.VIN_ACTIVE_DASHBOARD, param['vin']);
        this.sessionService.setItem(StorageKeys.SOURCE, param['source']);

        this.hasParams = true;
        this.initialize();
      },
      error: (e) => {
        console.error(e)
      }
    });
  }

  async initialize() {
    const country = this.sessionService.getItem(StorageKeys.COUNTRY);
    const token = this.sessionService.getItem('token');
    const cat = this.sessionService.getItem(StorageKeys.CAT);
    const source = this.sessionService.getItem(StorageKeys.SOURCE);
    const vin =  this.sessionService.getItem(StorageKeys.VIN_ACTIVE_DASHBOARD);
    const scheduleId = this.sessionService.getItem('scheduleId');

    if (!token || !cat) {
      return;
    }

    this.#tokens = {
      token: token,
      cat: cat
    };

    this.#urlInfo = {
      source: source,
      country: country,
      vin: vin,
      scheduleId: scheduleId ? scheduleId : undefined,
    };

    this.auth.tokens = this.#tokens;
    this.auth.urlInfo = this.#urlInfo;

    this.getLang(country);
    this.isSourceFordPass = isSourceFordPass(source);
    this.isPerks = isSourcePerks(source);

    try {
      this.showProject = (await firstValueFrom(this.featureToggle.getList(country))).OSR_FLOW_DISPLAY_STATUS?.active || false;
      localStorage.setItem('maintenanceWindow', `${!this.showProject}`);
    } catch (error) {
      console.error('Error fetching feature toggle:', error);
      this.showProject = false;
      localStorage.setItem('maintenanceWindow', 'true');
    }

    if (!this.showProject) {
      this.router.navigate(['/maintenance']);
      return;
    }

    await this.#coreUserService.loadUserProfile();
    await this.#myGarageService.loadUserVehicles();

    const response = this.#coreUserService.userComputed();
    if (response) {
      this.#coreUserService.saveUserProfile(response);
    }

    if (
      this.#urlInfo.scheduleId
      && this.#urlInfo.scheduleId !== null
      && this.#urlInfo.scheduleId !== undefined
    ) {
      sessionStorage.setItem('scheduleId', this.#urlInfo.scheduleId)
    }
  }

  getLang(country: string) {
    if (!country) {
      return;
    }

    sessionStorage.setItem('country', country);
    const tempLang = sessionStorage.getItem('tempLang');
    this.language = getLangByCountry(country || '');

    if (tempLang) {
      this.language = tempLang;
    }

    this.translate.setDefaultLang(this.language);
    this.translate.use(this.language);
  }
}
